import type { useI18n } from 'vue-i18n';
import type { NativeDateInput } from './dates';
import { Temporal } from 'temporal-polyfill';
import { translateString } from '#layers/i18n/public';
import { toNativeDate } from './dates';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_MINUTE_MS = 60 * 1000;
const RELATIVE_TIME_FORMAT = new Intl.RelativeTimeFormat('en', { style: 'long' });

function getCurrentLocalPlainDate(): Temporal.PlainDate {
	return plainDateFromLocalDate(new Date());
}

export interface FormatRelativeTimeOptions {
	granularity?: 'minute';
}

export function formatRelativeTime(date: NativeDateInput, i18n: ReturnType<typeof useI18n>, options?: FormatRelativeTimeOptions): string {
	const nativeDate = toNativeDate(date);

	if (options?.granularity !== 'minute') {
		return formatRelativePlainDate(plainDateFromLocalDate(nativeDate), i18n);
	}

	return formatRelativeEpochMilliseconds(nativeDate.getTime(), i18n);
}

export function formatRelativePlainDate(date: Temporal.PlainDate, i18n: ReturnType<typeof useI18n>): string {
	const today = getCurrentLocalPlainDate();
	const dayDifference = date.since(today, { largestUnit: 'days' }).days;

	if (dayDifference === 0) {
		return translateString('$t:time.today', i18n).toLocaleLowerCase();
	}

	return RELATIVE_TIME_FORMAT.format(dayDifference, 'days');
}

export function formatRelativePlainDateTime(
	dateTime: Temporal.PlainDateTime,
	i18n: ReturnType<typeof useI18n>,
	options?: FormatRelativeTimeOptions,
): string {
	if (options?.granularity !== 'minute') {
		return formatRelativePlainDate(dateTime.toPlainDate(), i18n);
	}

	const instant = dateTime.toZonedDateTime(Temporal.Now.timeZoneId()).toInstant();
	return formatRelativeTime(instant, i18n, options);
}

function formatRelativeEpochMilliseconds(epochMilliseconds: number, i18n: ReturnType<typeof useI18n>): string {
	const now = Date.now();
	const difference = epochMilliseconds - now;
	const absoluteDifference = Math.abs(difference);

	if (absoluteDifference < ONE_HOUR_MS) {
		const minuteDifference = Math.round(difference / ONE_MINUTE_MS);
		return minuteDifference === 0
			? translateString('$t:time.just_now', i18n).toLocaleLowerCase()
			: RELATIVE_TIME_FORMAT.format(minuteDifference, 'minutes');
	}

	if (absoluteDifference < ONE_DAY_MS) {
		return RELATIVE_TIME_FORMAT.format(Math.round(difference / ONE_HOUR_MS), 'hours');
	}

	return formatRelativePlainDate(plainDateFromLocalDate(new Date(epochMilliseconds)), i18n);
}

function plainDateFromLocalDate(date: Date): Temporal.PlainDate {
	return new Temporal.PlainDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
}
